import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { DocsLayout } from "./layouts/DocsLayout";
import { DocsProvider } from "./components/MDXProvider";
import DocsHome from "./pages/DocsHome";

// MDX imports - add a new lazy import + route for each new page
const FlowsIndex = lazy(() => import("./content/flows/index.mdx"));
const FlowsQuickstart = lazy(() => import("./content/flows/quickstart.mdx"));
const FlowsConcepts = lazy(() => import("./content/flows/concepts.mdx"));
const FlowsAgents = lazy(() => import("./content/flows/agents.mdx"));
const FlowsGraph = lazy(() => import("./content/flows/graph.mdx"));
const FlowsAgentSettings = lazy(() => import("./content/flows/agent-settings.mdx"));
const FlowsSubAgents = lazy(() => import("./content/flows/sub-agents.mdx"));
const FlowsConnections = lazy(() => import("./content/flows/connections.mdx"));
const FlowsSkills = lazy(() => import("./content/flows/skills.mdx"));
const FlowsTools = lazy(() => import("./content/flows/tools.mdx"));
const FlowsMcpServers = lazy(() => import("./content/flows/mcp-servers.mdx"));
const FlowsPlayground = lazy(() => import("./content/flows/playground.mdx"));
const FlowsSchedules = lazy(() => import("./content/flows/schedules.mdx"));
const FlowsMessaging = lazy(() => import("./content/flows/messaging.mdx"));
const FlowsCallApi = lazy(() => import("./content/flows/call-api.mdx"));
const FlowsA2A = lazy(() => import("./content/flows/a2a.mdx"));
const FlowsVersions = lazy(() => import("./content/flows/versions.mdx"));
const FlowsExecutions = lazy(() => import("./content/flows/executions.mdx"));
const FlowsUsage = lazy(() => import("./content/flows/usage.mdx"));
const ChatIndex = lazy(() => import("./content/chat/index.mdx"));
const ChatQuickstart = lazy(() => import("./content/chat/quickstart.mdx"));
const ChatConcepts = lazy(() => import("./content/chat/concepts.mdx"));
const ChatSelectingAgent = lazy(() => import("./content/chat/selecting-agent.mdx"));
const ChatSessions = lazy(() => import("./content/chat/sessions.mdx"));
const ChatMetaAgent = lazy(() => import("./content/chat/meta-agent.mdx"));
const ChatBuildAgent = lazy(() => import("./content/chat/build-agent.mdx"));
const ChatRefineAgent = lazy(() => import("./content/chat/refine-agent.mdx"));
const ChatSlashCommands = lazy(() => import("./content/chat/slash-commands.mdx"));
const ChatFileUploads = lazy(() => import("./content/chat/file-uploads.mdx"));
const AdminIndex = lazy(() => import("./content/admin/index.mdx"));
const AdminDashboard = lazy(() => import("./content/admin/dashboard.mdx"));
const AdminAgentBuilder = lazy(() => import("./content/admin/agent-builder.mdx"));
const AdminIntegrations = lazy(() => import("./content/admin/integrations.mdx"));
const AdminConnectingServices = lazy(() => import("./content/admin/connecting-services.mdx"));
const AdminOAuth = lazy(() => import("./content/admin/oauth.mdx"));
const AdminKnowledgeBase = lazy(() => import("./content/admin/knowledge-base.mdx"));
const AdminManagingFiles = lazy(() => import("./content/admin/managing-files.mdx"));
const AdminTeam = lazy(() => import("./content/admin/team.mdx"));
const AdminSettings = lazy(() => import("./content/admin/settings.mdx"));
const McpServerIndex = lazy(() => import("./content/mcp-server/index.mdx"));
const McpServerQuickstart = lazy(() => import("./content/mcp-server/quickstart.mdx"));
const McpServerConcepts = lazy(() => import("./content/mcp-server/concepts.mdx"));
const McpServerAgentLifecycle = lazy(() => import("./content/mcp-server/agent-lifecycle.mdx"));
const McpServerExecution = lazy(() => import("./content/mcp-server/execution.mdx"));
const McpServerProviders = lazy(() => import("./content/mcp-server/providers.mdx"));
const McpServerServers = lazy(() => import("./content/mcp-server/servers.mdx"));
const McpServerSkillsTools = lazy(() => import("./content/mcp-server/skills-tools.mdx"));
const McpServerUtility = lazy(() => import("./content/mcp-server/utility.mdx"));
const RuntimeIndex = lazy(() => import("./content/runtime/index.mdx"));
const RuntimeExecutionModel = lazy(() => import("./content/runtime/execution-model.mdx"));
const RuntimeTransferVsDelegate = lazy(() => import("./content/runtime/transfer-vs-delegate.mdx"));
const RuntimeRoutingGraphDesign = lazy(() => import("./content/runtime/routing-graph-design.mdx"));
const RuntimeCorrectionNudges = lazy(() => import("./content/runtime/correction-nudges.mdx"));
const RuntimeLimitsAndBudgets = lazy(() => import("./content/runtime/limits-and-budgets.mdx"));
const RuntimeRoutingTimeline = lazy(() => import("./content/runtime/routing-timeline.mdx"));
const RuntimePermissions = lazy(() => import("./content/runtime/permissions.mdx"));
const RuntimeMemory = lazy(() => import("./content/runtime/memory.mdx"));
const RuntimeResumeStreaming = lazy(() => import("./content/runtime/resume-streaming.mdx"));
const RuntimeAgentToAgent = lazy(() => import("./content/runtime/agent-to-agent.mdx"));

// Wrap an MDX file in the shared layout
function DocPage({ children }: { children: React.ReactNode }) {
  return (
    <DocsProvider>
      <DocsLayout>{children}</DocsLayout>
    </DocsProvider>
  );
}

export default function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Docs homepage - no sidebar */}
        <Route
          path="/docs"
          element={
            <DocsProvider>
              <DocsLayout>
                <DocsHome />
              </DocsLayout>
            </DocsProvider>
          }
        />

        {/* Flows */}
        <Route
          path="/docs/flows"
          element={
            <DocPage>
              <FlowsIndex />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/quickstart"
          element={
            <DocPage>
              <FlowsQuickstart />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/concepts"
          element={
            <DocPage>
              <FlowsConcepts />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/agents"
          element={
            <DocPage>
              <FlowsAgents />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/graph"
          element={
            <DocPage>
              <FlowsGraph />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/agent-settings"
          element={
            <DocPage>
              <FlowsAgentSettings />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/sub-agents"
          element={
            <DocPage>
              <FlowsSubAgents />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/connections"
          element={
            <DocPage>
              <FlowsConnections />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/skills"
          element={
            <DocPage>
              <FlowsSkills />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/tools"
          element={
            <DocPage>
              <FlowsTools />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/mcp-servers"
          element={
            <DocPage>
              <FlowsMcpServers />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/playground"
          element={
            <DocPage>
              <FlowsPlayground />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/schedules"
          element={
            <DocPage>
              <FlowsSchedules />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/messaging"
          element={
            <DocPage>
              <FlowsMessaging />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/call-api"
          element={
            <DocPage>
              <FlowsCallApi />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/a2a"
          element={
            <DocPage>
              <FlowsA2A />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/versions"
          element={
            <DocPage>
              <FlowsVersions />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/executions"
          element={
            <DocPage>
              <FlowsExecutions />
            </DocPage>
          }
        />
        <Route
          path="/docs/flows/usage"
          element={
            <DocPage>
              <FlowsUsage />
            </DocPage>
          }
        />

        {/* Chat */}
        <Route
          path="/docs/chat"
          element={
            <DocPage>
              <ChatIndex />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/quickstart"
          element={
            <DocPage>
              <ChatQuickstart />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/concepts"
          element={
            <DocPage>
              <ChatConcepts />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/selecting-agent"
          element={
            <DocPage>
              <ChatSelectingAgent />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/sessions"
          element={
            <DocPage>
              <ChatSessions />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/meta-agent"
          element={
            <DocPage>
              <ChatMetaAgent />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/build-agent"
          element={
            <DocPage>
              <ChatBuildAgent />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/refine-agent"
          element={
            <DocPage>
              <ChatRefineAgent />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/slash-commands"
          element={
            <DocPage>
              <ChatSlashCommands />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/file-uploads"
          element={
            <DocPage>
              <ChatFileUploads />
            </DocPage>
          }
        />

        {/* Admin */}
        <Route
          path="/docs/admin"
          element={
            <DocPage>
              <AdminIndex />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/dashboard"
          element={
            <DocPage>
              <AdminDashboard />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/agent-builder"
          element={
            <DocPage>
              <AdminAgentBuilder />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/integrations"
          element={
            <DocPage>
              <AdminIntegrations />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/connecting-services"
          element={
            <DocPage>
              <AdminConnectingServices />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/oauth"
          element={
            <DocPage>
              <AdminOAuth />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/knowledge-base"
          element={
            <DocPage>
              <AdminKnowledgeBase />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/managing-files"
          element={
            <DocPage>
              <AdminManagingFiles />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/team"
          element={
            <DocPage>
              <AdminTeam />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/settings"
          element={
            <DocPage>
              <AdminSettings />
            </DocPage>
          }
        />

        {/* MCP Server */}
        <Route
          path="/docs/mcp-server"
          element={
            <DocPage>
              <McpServerIndex />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/quickstart"
          element={
            <DocPage>
              <McpServerQuickstart />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/concepts"
          element={
            <DocPage>
              <McpServerConcepts />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/agent-lifecycle"
          element={
            <DocPage>
              <McpServerAgentLifecycle />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/execution"
          element={
            <DocPage>
              <McpServerExecution />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/providers"
          element={
            <DocPage>
              <McpServerProviders />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/servers"
          element={
            <DocPage>
              <McpServerServers />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/skills-tools"
          element={
            <DocPage>
              <McpServerSkillsTools />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/utility"
          element={
            <DocPage>
              <McpServerUtility />
            </DocPage>
          }
        />

        {/* Runtime */}
        <Route
          path="/docs/runtime"
          element={
            <DocPage>
              <RuntimeIndex />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/execution-model"
          element={
            <DocPage>
              <RuntimeExecutionModel />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/transfer-vs-delegate"
          element={
            <DocPage>
              <RuntimeTransferVsDelegate />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/routing-graph-design"
          element={
            <DocPage>
              <RuntimeRoutingGraphDesign />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/correction-nudges"
          element={
            <DocPage>
              <RuntimeCorrectionNudges />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/limits-and-budgets"
          element={
            <DocPage>
              <RuntimeLimitsAndBudgets />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/routing-timeline"
          element={
            <DocPage>
              <RuntimeRoutingTimeline />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/permissions"
          element={
            <DocPage>
              <RuntimePermissions />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/memory"
          element={
            <DocPage>
              <RuntimeMemory />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/resume-streaming"
          element={
            <DocPage>
              <RuntimeResumeStreaming />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/agent-to-agent"
          element={
            <DocPage>
              <RuntimeAgentToAgent />
            </DocPage>
          }
        />

        {/* Placeholder routes - add MDX files as you create them */}
        <Route
          path="/docs/flows/*"
          element={
            <DocPage>
              <ComingSoon />
            </DocPage>
          }
        />
        <Route
          path="/docs/admin/*"
          element={
            <DocPage>
              <ComingSoon />
            </DocPage>
          }
        />
        <Route
          path="/docs/chat/*"
          element={
            <DocPage>
              <ComingSoon />
            </DocPage>
          }
        />
        <Route
          path="/docs/mcp-server/*"
          element={
            <DocPage>
              <ComingSoon />
            </DocPage>
          }
        />
        <Route
          path="/docs/runtime/*"
          element={
            <DocPage>
              <ComingSoon />
            </DocPage>
          }
        />

        {/* Redirect root and any unmatched routes to docs */}
        <Route path="/" element={<Navigate to="/docs" replace />} />
        <Route path="*" element={<Navigate to="/docs" replace />} />
      </Routes>
    </Suspense>
  );
}

function ComingSoon() {
  return (
    <div className="py-20 text-center">
      <p className="text-sm text-text-faint">This page is coming soon.</p>
    </div>
  );
}
